import React, { useState, useEffect, useCallback } from 'react';
import './energy.css'
import * as XLSX from 'xlsx';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { toast } from 'sonner';
import { useAbility } from '../../roles/AbilityContext';
import { useTranslation } from 'react-i18next';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Company, Energy, Vessels } from '../../interfaces/interfaces';
import { getCompany, getVessel, postEnergy } from '../../services/requests';
import { useTheme } from '../../themeContext';
import { ChartOptionsBuilder } from '../../components/graphics/chartOptions';
import ReactECharts from 'echarts-for-react';

const EnergyComponent: React.FC = () => {
    const [filterCompany, setFilterCompany] = useState('companyGlobalView');
    const [filterCompany2, setFilterCompany2] = useState('');
    const [filterVessel, setFilterVessel] = useState('vesselGlobalView');
    const [filterVessel2, setFilterVessel2] = useState('');
    const showResolved = 'energy';
    const [isFullScreenGraphicBars, setIsFullScreenGraphicBars] = useState(false);
    const [isFullScreenTable, setIsFullScreenTable] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dataVessels, setDataVessels] = useState([{ Tembarcacion: "", IDregistro: "", Nembarcacion: 0, Tipo: 0, AManufactura: 0, Ffabricacion: "", Tamanno: 0, Foto: "", Nempresa: 0 }]);
    const [dataVesselsTembarcacion, setDataVesselsTembarcacion] = useState<string[]>([]);
    const [dataCompany, setDataCompany] = useState([{ Nempresa: 0, Tempresa: "", CIF: "", Tdireccion: "", Latitud: "", Longitud: "", Tcontacto: "", PhoneContacto: "", Tcorreo: "", Lenguaje: "", Falta: "" }]);
    const [dataCompanyTempresa, setDataCompanyTempresa] = useState<string[]>([]);
    const ability = useAbility();
    const [dataTable, setDataTable] = useState([{ Date: "", kWh: "" }]);
    const [totalEnergy, setTotalEnergy] = useState("");
    const [totalCost, setTotalCost] = useState("");
    const { t } = useTranslation();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const [startDate, setStartDate] = useState<Date | null>(sevenDaysAgo);
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    /**
     * Obtiene información de embarcaciones y empresas desde la API, y actualiza los estados correspondientes.
     * - Extrae los IDs de empresa y embarcación desde cookies.
     * - Llama a `getInformationEnergy` para cargar datos energéticos.
     * - Obtiene las listas de embarcaciones y empresas desde la API.
     * - Filtra y transforma los datos para popular listas de nombres (`Tembarcacion`, `Tempresa`).
     */
    const getInformation = useCallback(async () => {
        setLoading(true);
        try {
            const empresaID = Number(Cookies.get("Nempresa"));
            const embarcacionID = Number(Cookies.get("Nembarcacion"));
            getInformationEnergy(startDate?.toISOString() ?? '', endDate?.toISOString() ?? '', isNaN(empresaID) ? 0 : empresaID, isNaN(embarcacionID) ? 0 : embarcacionID);

            const responseVessel = await getVessel();

            const responseCompany = await getCompany();

            setDataVessels(responseVessel.data.data as Vessels[]);

            const filteredDataVessels = empresaID === 0 ? (responseVessel.data.data as Vessels[]) : (responseVessel.data.data as Vessels[]).filter(item => item.Nempresa === empresaID);
            const listTembarcacion = filteredDataVessels?.length ? Array.from(new Set(filteredDataVessels.map(item => item.Tembarcacion))).sort() : [];
            setDataVesselsTembarcacion(listTembarcacion);

            setDataCompany(responseCompany.data.data as Company[]);
            const empresasConVessels = new Set((responseVessel.data.data as Vessels[]).map(vessel => vessel.Nempresa));
            const empresasFiltradas = (responseCompany.data.data as Company[]).filter(company => empresasConVessels.has(company.Nempresa));
            const listTempresa = empresasFiltradas.length ? Array.from(new Set(empresasFiltradas.map(item => item.Tempresa))).sort() : [];
            setDataCompanyTempresa(listTempresa);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Obtiene información energética (kWh, kW, A, Cost) de la API y actualiza el estado de la tabla y totales.
     * @param Finicio - Fecha de inicio en formato ISO.
     * @param Ffinal - Fecha final en formato ISO.
     * @param Nempresa - ID de la empresa.
     * @param Nembarcacion - ID de la embarcación.
     * - Realiza una petición POST a `/energy/` con los parámetros proporcionados.
     * - Transforma y guarda los datos energéticos en el estado.
     */
    const getInformationEnergy = async (Finicio: string, Ffinal: string, Nempresa: number, Nembarcacion: number) => {
        setLoading(true);

        try {

            const body = {
                Nempresa: Nempresa,
                Nembarcacion: Nembarcacion,
                Ffinal: Ffinal,
                Finicio: Finicio
            }

            const responseEnergy = await postEnergy(body);

            if (responseEnergy.status >= 200 && responseEnergy.status < 300) {
                toast.success(`${responseEnergy.data.message}`);
            }

            setDataTable(
                responseEnergy.data.data.electricalVar.map((electrical: Energy["electricalVar"][number]) => ({
                    Date: electrical.Date.toString(),
                    kWh: electrical.kWh.toFixed(2),
                    // kW: electrical.kW.toFixed(2),
                    // A: electrical.A.toFixed(2),
                }))
            );
            setTotalEnergy(responseEnergy.data.data.totalEnergy.toFixed(2));
            setTotalCost(responseEnergy.data.data.totalCost.toFixed(2));

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Efecto que ejecuta `getInformation` al montar el componente
     * o cuando cambia alguna de sus dependencias.
     */
    useEffect(() => {
        getInformation();
    }, [getInformation]);

    /**
     * Lista de categorías para el gráfico de barras, basada en las fechas de los registros.
     */
    const barCategories = dataTable.map((item) => item.Date);

    /**
     * Lista de valores numéricos para el gráfico de barras, basada en el tipo de dato seleccionado.
     * - Si `showResolved` es 'energy', se usa `kWh`.
     * - Si es 'power', se usa `kW`.
     * - En otros casos, se usa `A` (amperios).
     */
    const barData = dataTable.map((item) =>
        // parseFloat(showResolved === 'energy' ? item.kWh : showResolved === 'power' ? item.kW : item.A)
        parseFloat(showResolved === 'energy' ? item.kWh : "")
    );

    /**
     * Descarga los datos del gráfico como un archivo Excel.
     * @param categories - Lista de categorías (fechas).
     * @param data - Lista de valores numéricos.
     * @param title - Título de la hoja y nombre del archivo Excel.
     */
    const downloadDataGraphicAsXLSX = (categories: string[], data: number[], title: string) => {
        const worksheetData = categories.map((category, index) => ({
            Category: category,
            Value: data[index],
        }));
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title);
        XLSX.writeFile(workbook, `${title}.xlsx`);
    };

    /**
     * Descarga el contenido actual de la tabla de datos energéticos como archivo Excel.
     */
    const downloadDataTableAsXLSX = () => {
        const worksheet = XLSX.utils.json_to_sheet(dataTable);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Energy Consumption Table');
        XLSX.writeFile(workbook, 'Energy Consumption Table.xlsx');
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [sortedData, setSortedData] = useState([...dataTable]);
    const totalPage = itemsPerPage === sortedData.length ? 1 : Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === sortedData.length ? sortedData.length : startIndex + itemsPerPage;
    const currentData = sortedData.slice(startIndex, endIndex);

    /**
     * Avanza una página si aún no se ha alcanzado la última.
     */
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Retrocede una página si no está ya en la primera.
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const [sortConfig, setSortConfig] = useState<{
        key: keyof typeof dataTable[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "Date", direction: "desc" });

    /**
     * Ordena la tabla de datos (`dataTable`) por fecha de forma descendente
     * cuando el estado cambia.
     */
    useEffect(() => {
        const sorted = [...dataTable].sort((a, b) => (a.Date < b.Date ? 1 : -1));
        setSortedData(sorted);
    }, [dataTable]);

    /**
     * Ordena los datos de la tabla según la columna proporcionada.
     * @param key - Nombre de la propiedad del objeto a ordenar (ej. "Date", "kWh").
     * - Alterna entre ascendente y descendente al hacer clic repetido.
     */
    const sortData = (key: keyof typeof dataTable[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sorted = [...sortedData].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setSortedData(sorted);
    };

    /**
     * Filtra la lista de tipos de embarcaciones (`Tembarcacion`) según
     * la empresa seleccionada (`filterCompany2` o desde cookies).
     * - Si `Nempresa === 0`, usa `filterCompany2` para obtener el ID real.
     * - Actualiza la lista de tipos de embarcaciones disponibles.
     */
    useEffect(() => {
        let Nempresa: number;
        const empresaID = Number(Cookies.get("Nempresa"));
        if (empresaID === 0) {
            const filteredDataCompany = dataCompany.filter(item => item.Tempresa === filterCompany2);
            Nempresa = filteredDataCompany[0]?.Nempresa || 0;
        } else {
            Nempresa = empresaID;
        }

        if (Nempresa > 0) {
            const filteredDataVessels = dataVessels.filter(item => item.Nempresa === Nempresa);
            const listTembarcacion = filteredDataVessels?.length ? Array.from(new Set(filteredDataVessels.map(item => item.Tembarcacion))).sort() : [];
            setDataVesselsTembarcacion(listTembarcacion);
        } else {
            const listTembarcacion = dataVessels?.length ? Array.from(new Set(dataVessels.map(item => item.Tembarcacion))).sort() : [];
            setDataVesselsTembarcacion(listTembarcacion);
        }

    }, [filterCompany2, dataCompany, dataVessels]);

    const { isDarkTheme } = useTheme();

    /**
     * Construye las opciones para el gráfico de barras de energía utilizando `ChartOptionsBuilder.createBarChartOption2`.
     * - `barData`: datos numéricos que se graficarán.
     * - `barCategories`: etiquetas del eje X.
     * - El nombre de la serie se determina dinámicamente según `showResolved`:
     *    - Si es `"energy"`, muestra `Energy (kWh)`.
     *    - Si es `"power"`, muestra `Power (kW)`.
     *    - En otro caso, muestra `Current (A)`.
     * - `isDarkTheme`: define si el gráfico se renderiza en modo oscuro.
     */
    const optionsBar = ChartOptionsBuilder.createBarChartOption2(barData, barCategories, showResolved === "energy" ? [`${t('energy.energy')} (kWh)`] : showResolved === "power" ? [`${t('energy.power')} (kW)`] : [`${t('energy.current')} (A)`], isDarkTheme);

    /**
     * Aplica los filtros seleccionados para consultar información energética.
     * - Verifica que las fechas `startDate` y `endDate` estén definidas.
     * - Determina el ID de la empresa (`empresaID`) basado en:
     *    - El filtro manual `filterCompany2` si `filterCompany === "byCompany"`.
     *    - La cookie `Nempresa`, en caso contrario.
     * - Determina el ID de la embarcación (`embarcacionID`) basado en:
     *    - El filtro manual `filterVessel2` si `filterVessel === "byVessel"`.
     *    - La cookie `Nembarcacion`, en caso contrario.
     * - Llama a `getInformationEnergy` con las fechas y los IDs para obtener los datos.
     */
    const handleApplyFilters = () => {
        if (!startDate || !endDate) {
            toast.error(`${t('incidents.pleaseCompleteAllFields')}.`);
            return;
        }

        let empresaID: number = 0;
        let embarcacionID: number = 0;

        const empresaCookie = Cookies.get("Nempresa");
        const embarcacionCookie = Number(Cookies.get("Nembarcacion"));

        if (filterCompany === "byCompany") {
            const companyMatch = dataCompany.find(item => item.Tempresa === filterCompany2);
            empresaID = companyMatch ? companyMatch.Nempresa : 0;
        } else if (empresaCookie && !isNaN(Number(empresaCookie))) {
            empresaID = Number(empresaCookie);
        }

        if (filterVessel === "byVessel") {
            const vesselMatch = dataVessels.find(item => item.Tembarcacion === filterVessel2);
            embarcacionID = vesselMatch ? vesselMatch.Nembarcacion : 0;
        } else if (embarcacionCookie && !isNaN(Number(embarcacionCookie))) {
            embarcacionID = Number(embarcacionCookie);
        }

        getInformationEnergy(
            startDate.toISOString(),
            endDate.toISOString(),
            empresaID,
            embarcacionID
        );
    };

    return (
        <div className='containerEnergy'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('energy.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('energy.energyConsumption')}</h4>
                    </div>

                    <div className='filter'>
                        <div className='calendar'>
                            <DatePicker
                                selected={startDate}
                                onChange={(dates: [Date | null, Date | null]) => {
                                    const [start, end] = dates;
                                    setStartDate(start);
                                    setEndDate(end);
                                }}
                                startDate={startDate}
                                endDate={endDate}
                                selectsRange
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={new Date()}
                            />
                        </div>

                        {ability.can('read', 'AdminPage') && (
                            <select
                                value={filterCompany}
                                onChange={(e) => {
                                    setFilterCompany(e.target.value);
                                    setFilterCompany2(dataCompanyTempresa[0]);
                                    setFilterVessel("vesselGlobalView");
                                }}
                            >
                                <option value="companyGlobalView">{t('energy.companyGlobalView')}</option>
                                <option value="byCompany">{t('energy.byCompany')}</option>
                            </select>
                        )}

                        {ability.can('read', 'AdminPage') && filterCompany === "byCompany" && (
                            <select
                                value={filterCompany2}
                                onChange={(e) => {
                                    setFilterCompany2(e.target.value);
                                    setFilterVessel("vesselGlobalView");
                                }}
                            >
                                {dataCompanyTempresa.map((company) => (
                                    <option key={company} value={company}>{company}</option>
                                ))}
                            </select>
                        )}

                        {(ability.can('read', 'AdminPage') || ability.can('read', 'ManagerPage')) && (
                            <select
                                value={filterVessel}
                                onChange={(e) => {
                                    setFilterVessel(e.target.value);
                                    setFilterVessel2(dataVesselsTembarcacion[0]);
                                }}
                            >
                                <option value="vesselGlobalView">{t('energy.vesselGlobalView')}</option>
                                <option value="byVessel">{t('energy.byVessel')}</option>
                            </select>
                        )}

                        {(ability.can('read', 'AdminPage') || ability.can('read', 'ManagerPage')) && filterVessel === "byVessel" && (
                            <select
                                value={filterVessel2}
                                onChange={(e) => {
                                    setFilterVessel2(e.target.value);
                                }}
                            >
                                {dataVesselsTembarcacion.map((vessel) => (
                                    <option key={vessel} value={vessel}>{vessel}</option>
                                ))}
                            </select>
                        )}
                        <button onClick={handleApplyFilters}>
                            <i className="fas fa-search"></i>
                        </button>
                    </div>

                    <div className='separator1'>
                        <div className='card'>
                            <div>
                                <span>{t('energy.totalEnergy')}</span>
                                <span className='icon'><i className="fas fa-bolt"></i></span>
                            </div>
                            <span>{totalEnergy} kWh</span>
                        </div>

                        {/* <div className='card'>
                        <div>
                            <span>Max/Min Power</span>
                            <span className='icon'><i className="fa-solid fa-gauge-high"></i></span>
                        </div>
                        <span>98 / 1 kW</span>
                    </div> */}

                        {/* <div className='card'>
                            <div>
                                <span>{t('energy.totalCost')}</span>
                                <span className='icon'>€</span>
                            </div>
                            <span>€ {totalCost}</span>
                        </div> */}
                    </div>

                    {/* <div className='buttons' >
                    <button className={`btn ${showResolved === "energy" && 'active'}`} onClick={() => setShowResolved('energy')}>
                        Energy (kWh)
                    </button>
                    <button className={`btn ${showResolved === "power" && 'active'}`} onClick={() => setShowResolved('power')}>
                        Power (kW)
                    </button>
                    <button className={`btn ${showResolved === "current" && 'active'}`} onClick={() => setShowResolved('current')}>
                        Current (A)
                    </button>
                </div> */}

                    <div className='separator2'>
                        <div className={`card ${isFullScreenGraphicBars ? 'expanded' : ''}`}>
                            <div className='caption'>
                                <span className='title'>{showResolved === "energy" ? [t('energy.energy')] : showResolved === "power" ? [t('energy.power')] : [t('energy.current')]}  {t('energy.consumption')}</span>
                                <div className='icons'>
                                    <i
                                        className={`fas ${isFullScreenGraphicBars ? 'fa-compress' : 'fa-expand'}`}
                                        onClick={() => setIsFullScreenGraphicBars(!isFullScreenGraphicBars)}
                                        title={isFullScreenGraphicBars ? "Salir de Pantalla Completa" : "Pantalla Completa"}
                                    ></i>
                                    <i
                                        className="fas fa-file-excel"
                                        onClick={() => downloadDataGraphicAsXLSX(barCategories, barData, `${showResolved === "energy" ? "Energy" : showResolved === "power" ? "Power" : "Current"} Consumption`)}
                                        title="Descargar como Excel"
                                    ></i>
                                </div>
                            </div>
                            <div className='graphic'>
                                <div style={{ height: '100%', width: '100%' }}>
                                    <ReactECharts
                                        option={optionsBar}
                                        style={{ height: '100%', width: '99.9%' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={`card ${isFullScreenTable ? 'expanded' : ''}`}>
                            <div className='caption'>
                                <span className='title'>{t('energy.dataTable')}</span>
                                <div className='right'>
                                    <button className="btn btn-dark download-btn" onClick={() => downloadDataTableAsXLSX()}>
                                        <i className="fas fa-download"></i>
                                    </button>

                                    <button className="btn btn-dark fullscreen-btn" onClick={() => {
                                        setIsFullScreenTable(!isFullScreenTable);
                                    }}>
                                        <i className={`fas ${isFullScreenTable ? 'fa-compress' : 'fa-expand'}`}></i>
                                    </button>
                                </div>
                            </div>
                            <div className='tableConsumption'>
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th onClick={() => sortData("Date")}>
                                                {t('energy.date')} {sortConfig.key === "Date" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("kWh")}>
                                                kWh {sortConfig.key === "kWh" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            {/* <th onClick={() => sortData("kW")}>
                                                kW {sortConfig.key === "kW" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("A")}>
                                                A {sortConfig.key === "A" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th> */}
                                            {/* <th onClick={() => sortData("Cost")}>
                                                {t('energy.cost')} (€) {sortConfig.key === "Cost" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                                </th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.Date}</td>
                                                <td>{row.kWh}</td>
                                                {/* <td>{row.kW}</td>
                                                    <td>{row.A}</td> */}
                                                {/* <td>{row.Cost}</td> */}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pagination">
                                <button className="btn btn-secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                                    <i className="fas fa-chevron-left"></i> {t('energy.previous')}
                                </button>
                                <div>
                                    <span>{t('energy.page')} {currentPage} {t('energy.of')} {totalPage},</span>
                                    <div className="pagination-controls">
                                        <span>{t('energy.rowsPerPage')}:</span>
                                        <select
                                            className="form-control rows-select"
                                            value={
                                                itemsPerPage === dataTable.length && ![5, 10, 15, 50].includes(itemsPerPage)
                                                    ? "All"
                                                    : itemsPerPage
                                            }
                                            onChange={(e) => {
                                                const value = e.target.value === "All" ? dataTable.length : parseInt(e.target.value, 10);
                                                setItemsPerPage(value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="15">15</option>
                                            <option value="50">50</option>
                                            <option value="All">{t('energy.all')}</option>
                                        </select>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleNextPage}
                                    disabled={currentPage === Math.ceil(dataTable.length / itemsPerPage)}
                                >
                                    {t('energy.next')} <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

};

export default EnergyComponent;